import React, { useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';
import { CustomGarage, Solicitud } from '../types/ot.types';

interface TicketWhatsAppProps {
    taller: CustomGarage;
    solicitud: Solicitud;
    fechaCita: string;
}

export const TicketWhatsApp: React.FC<TicketWhatsAppProps> = ({ taller, solicitud, fechaCita }) => {
    const ticketRef = useRef<HTMLDivElement>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const searchQuery = encodeURIComponent(`${taller.nombre_completo} ${taller.sucursal} ${solicitud.ciudad || ''}`);
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${searchQuery}`;

    const handleDownloadImage = async () => {
        if (!ticketRef.current) return;
        setIsGenerating(true);
        try {
            const canvas = await html2canvas(ticketRef.current, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff'
            });
            const image = canvas.toDataURL("image/jpeg", 1.0);

            const link = document.createElement('a');
            link.href = image;
            link.download = `Pase_Taller_${solicitud.folio}.jpg`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error("Error generando imagen del ticket", error);
            alert("Hubo un error al generar la imagen. Inténtalo de nuevo.");
        } finally {
            setIsGenerating(false);
        }
    };

    const formatFecha = (fechaStr: string) => {
        if (!fechaStr) return 'Pendiente';
        try {
            const date = new Date(fechaStr);
            return date.toLocaleString('es-MX', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (e) {
            return fechaStr;
        }
    };

    return (
        <div className="mt-3">
            <div className="d-flex flex-column align-items-start">
                <div
                    id="ot-step2-voucher-ticket"
                    ref={ticketRef}
                    className="p-4 bg-white mb-3"
                    style={{
                        width: '380px',
                        maxWidth: '100%',
                        border: '2px dashed #adb5bd',
                        borderRadius: '12px',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
                        position: 'relative',
                        overflow: 'hidden'
                    }}
                >
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '6px', backgroundColor: '#198754' }}></div>

                    <div className="text-center border-bottom pb-3 mb-3 mt-2">
                        <div className="d-inline-flex align-items-center justify-content-center bg-light rounded-circle mb-2" style={{ width: '50px', height: '50px' }}>
                            <i className="fas fa-car text-success fs-4"></i>
                        </div>
                        <h5 className="fw-bold mb-0 text-dark">Pase de Ingreso a Taller</h5>
                        <span className="badge bg-secondary mt-1 px-3 py-2 fs-7">OT: {solicitud.folio}</span>
                    </div>

                    <div className="text-start mb-4" style={{ fontSize: '0.9rem' }}>
                        <div id="ot-step2-voucher-destino" className="mb-2">
                            <span className="text-muted d-block" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Destino</span>
                            <strong className="d-block text-dark">{taller.nombre_completo}</strong>
                            <span className="text-secondary">{taller.sucursal || 'Matriz'}</span>
                        </div>

                        <div id="ot-step2-voucher-vehiculo" className="mb-2">
                            <span className="text-muted d-block" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Vehículo</span>
                            <strong className="d-block text-dark">{solicitud.marca} {solicitud.modelo} ({solicitud.anio})</strong>
                            <span className="text-secondary">Serie: {solicitud.numero_serie || 'N/A'}</span>
                        </div>

                        <div id="ot-step2-voucher-cita" className="mt-3 p-3 bg-light rounded border border-success border-opacity-25 text-center">
                            <span className="text-success d-block fw-bold mb-1" style={{ fontSize: '0.8rem', textTransform: 'uppercase' }}>
                                <i className="far fa-calendar-alt me-1"></i> Cita Programada
                            </span>
                            <strong className="text-dark d-block" style={{ fontSize: '0.95rem' }}>
                                {formatFecha(fechaCita).charAt(0).toUpperCase() + formatFecha(fechaCita).slice(1)}
                            </strong>
                        </div>
                    </div>

                    <div id="ot-step2-voucher-qr" className="d-flex flex-column align-items-center justify-content-center pt-2 border-top">
                        <small className="text-muted mt-2 fw-bold" style={{ fontSize: '0.7rem' }}>Escanea para abrir en Google Maps</small>
                    </div>
                </div>

                <button
                    id="ot-step2-voucher-descargar"
                    type="button"
                    className="btn btn-success fw-bold shadow-sm rounded-pill px-4"
                    onClick={handleDownloadImage}
                    disabled={isGenerating}
                >
                    {isGenerating ? (
                        <><i className="fas fa-spinner fa-spin me-2"></i> Generando imagen...</>
                    ) : (
                        <><i className="fab fa-whatsapp me-2 fs-5"></i> Descargar Pase para Compartir</>
                    )}
                </button>
            </div>
        </div>
    );
};